import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type RowClickedEvent, type RowClassParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControlLabel, FormControl, FormGroup, Radio, RadioGroup, Checkbox, MenuItem, Select, Tooltip} from "@mui/material";
import Barcode from "react-barcode";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";
import { useSSE, type ErrorEvent } from "@modules/sse";

import gridInputCellEditor from "@/components/aggrid/aggrid_input_cell_editor";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./workstation_manage.scoped.scss";
import modifyIcon from "@user/assets/images/common/write-secondary.svg";

import InboundInfoPopup from "@user/views/inoutbound/popup_inbound_info";
import SkuReleasePopup from "@user/views/inoutbound/popup_sku_release";
import SkuAddPopup from "@user/views/inoutbound/popup_sku_add";
import SkuCellPopup from "@user/views/inoutbound/popup_sku_cell";
import LoadcapaRatePopup from "@user/views/inoutbound/popup_loadcapa_rate";
import TrSnChangePopup from "@user/views/inoutbound/popup_tr_sn_change";

import PrintTrtag, { type PrintTrtagHandle } from "@user/views/print/print_trtag";
import PrintLabel, { type PrintLabelHandle } from "@user/views/print/print_label";
import PrintTicket, { type PrintTicketHandle } from "@user/views/print/print_ticket";

interface State {
  gridApi?: GridApi,
  barcodeType: string,
  skuHoldQty: string,
  isShowInboundInfo: boolean,
  isShowSkuRelease: boolean,
  isShowSkuAdd: boolean,
  isShowSkuCell: boolean,
  isShowLoadcapaRate: boolean,
  trSnInfo: object,
  isShowTrSnChange: boolean,
  isHoverBtn: boolean
}

const defState: State = {
  gridApi: undefined,
  barcodeType: "WORK_COMPLETE",
  skuHoldQty: "0",
  isShowInboundInfo: false,
  isShowSkuRelease: false,
  isShowSkuAdd: false,
  isShowSkuCell: false,
  isShowLoadcapaRate: false,
  trSnInfo: {},
  isShowTrSnChange: false,
  isHoverBtn: false
};

interface SearchParams {
  workStationId: string
}

const defSearchParams = {
  workStationId: ""
};

interface WorkStationInfo {
  workStationId: string,
  workStationType: string
}

const defWorkStationInfo = {
  workStationId: "",
  workStationType: ""
};

interface SkuInfo {
  skuNo: string,
  skuNoEx: string,
  skuType: string,
  skuSize: string,
  skuCellQty: string,
  skuCellUseQty: string,
  workType: string,
  workNoMfc: string,
  workRequesterName: string,
  workReqSortOrder: string,
  workReqTotalQty: string,
  workStationId: string
}

const defSkuInfo = {
  skuNo: "",
  skuNoEx: "",
  skuType: "",
  skuSize: "",
  skuCellQty: "",
  skuCellUseQty: "",
  workType: "",
  workNoMfc: "",
  workRequesterName: "",
  workReqSortOrder: "",
  workReqTotalQty: "",
  workStationId: ""
};

interface Result {
  searchParams: SearchParams,
  infos: any[],
  outInfos: any[],
  equipInfos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: [],
  outInfos: [],
  equipInfos: []
};

const validationMsg4completeWork = [
  {id: "(.*).jobQty", type: "NotNull", msg: "작업수량을 입력해 주세요."},
  {id: "(.*).jobQty", type: "Min", msg: "작업수량을 확인해 주세요."},
  {id: "(.*).jobQty", type: "Max", msg: "작업수량을 확인해 주세요."},
  {id: "reqInfos", type: "NotBlank", msg: "작업이 없습니다."},
  {id: "reqInfos", type: "InvalidStatus", msg: "작업이 올바르지 않습니다."},
  {id: "skuNo", type: "NotFound", msg: "SKU가 없습니다."},
  {id: "skuCellQty", type: "InvalidStatus", msg: "적재 가능 SKU Cell이 없습니다."},
  {id: "stockRate", type: "InvalidStatus", msg: "적재 가능 SKU Cell을 초과하였습니다."},
  {id: "pn", type: "InvalidStatus", msg: "다른 창고의 동일한 P/N은 입고할 수 없습니다."},
  {id: "stockInfo", type: "InvalidStatus", msg: "재고 정보를 찾을 수 없습니다."},
  {id: "jobQty", type: "InvalidStatus", msg: "요청수량을 초과하였습니다."},
  {id: "stockQty", type: "InvalidStatus", msg: "재고수량을 초과하였습니다."},
  {id: "remainQty", type: "InvalidStatus", msg: "전체 작업수량이 요청수량을 초과하였습니다."}
];

const validationMsg4reIn = [
  {id: "skuNo", type: "NotFound", msg: "재입고할 SKU가 없습니다."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."}
];

const validationMsg4holdSku = [
  {id: "holdYn", type: "InvalidStatus", msg: "이미 보류된 SKU입니다."}
];

const validationMsg4orderWork = [
  {id: "workReqNo", type: "InvalidStatus", msg: "이미 작업지시한 작업입니다."},
  {id: "skuNo", type: "NotFound", msg: "SKU가 없습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."},
  {id: "skuCellQty", type: "InvalidStatus", msg: "적재 가능 SKU Cell이 없습니다."},
  {id: "stockRate", type: "InvalidStatus", msg: "적재 가능 SKU Cell을 초과하였습니다."},
  {id: "pn", type: "InvalidStatus", msg: "다른 창고의 동일한 P/N은 입고할 수 없습니다."},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

const validationMsg4updateStockQty = [
  {id: "(.*).stockQty", type: "NotNull", msg: "Stock Qty를 입력해 주세요."},
  {id: "(.*).stockQty", type: "Min", msg: "Stock Qty를 확인해 주세요."},
  {id: "(.*).stockQty", type: "Max", msg: "Stock Qty를 확인해 주세요."},
  {id: "reqInfos", type: "NotBlank", msg: "수정할 재고가 없습니다."}
];

const defColDef = { ...kutil.defaultColDef, cellDataType: false };
const defColDef4equip: CustomColDef = {
  resizable: true,
  sortable: true,
  cellStyle: (p) => ({
    textAlign: p.colDef.headerClass == "hd-ta-right" ? "right" : p.colDef.headerClass == "hd-ta-left" ? "left" : "center",
    cursor: "pointer"
  })
};

const nz = (v: any) => (v ?? "").toString().trim();

function WorkstationManage(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [workStationInfo, setWorkStationInfo] = useState<WorkStationInfo>(kutil.deepCopy(defWorkStationInfo));
  const [skuInfo, setSkuInfo] = useState<SkuInfo>(kutil.deepCopy(defSkuInfo));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));
  const [currSkuNo, setCurrSkuNo] = useState<string>("");

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);
  const gridState4out = useMemo(() => useSettingsStore.getState().getGridState(location.path + ".out"), [location.path]);
  const gridState4equip = useMemo(() => useSettingsStore.getState().getGridState(location.path + ".equip"), [location.path]);

  const printTrtagRef = useRef<PrintTrtagHandle>(null);
  const printLabelRef = useRef<PrintLabelHandle>(null);
  const printTicketRef = useRef<PrintTicketHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const resultRef = useRef(result);
  useEffect(() => { resultRef.current = result; }, [result]);

  const skuInfoRef = useRef(skuInfo);
  useEffect(() => { skuInfoRef.current = skuInfo; }, [skuInfo]);

  const onSSEMessageWorkStations = useCallback((msg: MessageEvent) => {
    try {
      const result = JSON.parse(msg.data);
      const workStations = result.data;
      setCurrSkuNo(workStations.find((info: any) => info.workStationId === searchParams.workStationId)?.skuNo || "");
    } catch {
      console.error("SSE parse 실패:", msg.data);
    }
  }, [searchParams?.workStationId]);

  const onSSEError = useCallback((err: ErrorEvent) => {
    if (err.code == 400 || err.code == 401 || err.code == 403) {
        kutil.alert("로그인이 필요합니다.");
        kutil.removeRefreshToken();
        window.location.replace(window.location.href);
    }
    console.log(err);
  }, []);

  useSSE({
    url: "/api/sse/subscribe?eventName=work-stations&eventKey=",
    sessionId: useAuthStore.getState().sessionId,
    enabled: isActiveTab,
    messageHandlers: {
      "work-stations": onSSEMessageWorkStations,
    },
    onError: onSSEError,
  });

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "재고구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.workNo ? commonCode.getCodeValue("inoutType", p.data.inoutType) : "재고");
      }
    },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.workNo ? commonCode.getCodeValue("inoutDetailType", p.data.inoutDetailType) : "-");
      }
    },
    { headerName: "관리구분", field: "mngType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "TR/SN 변경", field: "", width: 90, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workNo && p.data.workReqErpNo) {
          if (!auth.write) return null;
          return (<button className="icon-button grid-write" onClick={() => updateTrSn(p.data)}>변경</button>);
        }
      }
    },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number,
      cellClass: (p: any) => !p.data.workNo ? ["input-cell", "ta-right"] : "",
      editable: (p: any) => !p.data.workNo,
      singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 }
    },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number,
      cellClass: (p: any) => p.data.workNo ? ["input-cell", "ta-right"] : "",
      editable: (p: any) => p.data.workNo,
      singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 }
    },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "Location", field: "locationId", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId },
    { headerName: "TR Tag", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workNo && p.data.trFileName) {
          if (!auth.download) return null;
          return (<button className="icon-button grid-print" onClick={() => printTrtag(p.data)}>출력</button>);
        }
      }
    },
    { headerName: "라벨출력", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workNo) {
          if (!auth.download) return null;
          return (<button className="icon-button grid-print" onClick={() => printLabel(p.data)}>출력</button>);
        }
      }
    },
    { headerName: "티켓출력", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workNo && p.data.ticketFileName) {
          if (!auth.download) return null;
          return (<button className="icon-button grid-print" onClick={() => printTicket(p.data)}>출력</button>);
        }
      }
    },
    { headerName: "작업취소", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workNo) {
          if (!auth.write) return null;
          return (<button className="icon-button grid-cancel" onClick={() => cancelWork(p.data)}>취소</button>);
        }
      }
    },
    { headerName: "오류내용", headerClass: "hd-ta-left", field: "", width: 250, cellStyle: {textAlign: "left"} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowStyle: (p: RowClassParams) => {
      if (p.data.workNo) {
        return {"backgroundColor": "#e0f0ff"};
      }
    },
    stopEditingWhenCellsLoseFocus: true
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  const gridInfo4out = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    {
      headerName: "작업추가", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (!auth.write) return null;
        return (<button className="icon-button grid-add" onClick={() => orderWork(p.data)}>추가</button>);
      }
    },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remain Qty", headerClass: "hd-ta-right", field: "remainQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Complete Qty", headerClass: "hd-ta-right", field: "completeQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "기번(HL)", field: "hl", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4out = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path + ".out");
    }
  };

  const gridInfo4equip = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Track No", field: "workNoMfc", width: 100, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업구분", field: "workType", width: 80, valueFormatter: valueFormatter.commonCode("workType") },
    { headerName: "창고", field: "wh", width: 80, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 100, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업대", field: "workStationId", width: 80, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Location", field: "startPos", width: 100, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "설비", field: "workEquip", width: 100, valueFormatter: valueFormatter.commonCode("workEquip") },
    { headerName: "설비상태", field: "workStatusEquip", width: 100,
      valueGetter: (p: any) => {
        return (p.data.workStatusEquip == "START" || p.data.workStatusEquip == "ACTIVE" ? p.data.currPos : commonCode.getCodeValue("workStatusEquip", p.data.workStatusEquip));
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4equip = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path + ".equip");
    },
    onRowClicked: (e: RowClickedEvent) => {
      doSearch(null, {workStationId: e.data.workStationId});
    }
  };

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능, 단축키 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  const onKeyboardShortcutRef = useRef(onKeyboardShortcut);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
    onKeyboardShortcutRef.current = onKeyboardShortcut;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowInboundInfo || state.isShowSkuRelease || state.isShowSkuAdd || state.isShowSkuCell || state.isShowLoadcapaRate || state.isShowTrSnChange) {
      return;
    }

    // 바코드 스캔 핸들러
    const barcodeHandler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(barcodeHandler);

    // 키보드 단축키 핸들러
    const shortcuthandler = (code: string) => onKeyboardShortcutRef.current(code);
    kutil.startKeyboardShortcut([
      {id: "F1", altKey: false, ctrlKey: false, shiftKey: false, key: "F1"},
      {id: "F2", altKey: false, ctrlKey: false, shiftKey: false, key: "F2"},
      {id: "F3", altKey: false, ctrlKey: false, shiftKey: false, key: "F3"},
    ], shortcuthandler);

    return () => {
      kutil.stopBarcodeScan(barcodeHandler)
      kutil.stopKeyboardShortcut(shortcuthandler)
    };
  }, [isActiveTab, state.isShowInboundInfo, state.isShowSkuRelease, state.isShowSkuAdd, state.isShowSkuCell, state.isShowLoadcapaRate, state.isShowTrSnChange]);
  // 끝: 바코드 스캔 기능, 단축키 적용

  useEffect(() => {
    console.log("changed : " + searchParams.workStationId + "/" + currSkuNo);
    if (searchParams.workStationId) {
      searchInfos();
    }
  }, [searchParams.workStationId, currSkuNo]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);

    if (!searchParams.workStationId) {
      const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path)
        || useSettingsStore.getState().getWorkStationId(location.path)
        || (commonCode.workStationId as any[])?.[0]?.code
        || "";
      searchParams.workStationId = workStationId ;
    }

    useSettingsStore.getState().setWorkStationId(location.path, searchParams.workStationId);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/workstation_manage.data?mode=search", reqSearchParams, {});
      setState(prev => ({ ...prev, skuHoldQty: data.skuHoldInfos.length }));
      setWorkStationInfo(data.workStationInfo);
      data.skuInfo.workType = data.skuInfo.workType || "";
      data.skuInfo.workNoMfc = data.skuInfo.workNoMfc || "";
      data.skuInfo.workRequesterName = data.skuInfo.workRequesterName || "";
      data.skuInfo.workReqSortOrder = data.skuInfo.workReqSortOrder || "";
      data.skuInfo.workReqTotalQty = data.skuInfo.workReqTotalQty || "";
      data.skuInfo.workStationId = data.skuInfo.workStationId || "";
      setSkuInfo(data.skuInfo);
      const infos = [...data.workInfos, ...data.skuStockInfos];
      setResult({
        searchParams: reqSearchParams,
        infos: infos,
        outInfos: data.workReqInfos,
        equipInfos: data.equipWorkInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams });
    router.navigateByPath("/inoutbound/workstation_manage", searchParams);
  }

  async function completeWork(forceYn: "N" | "Y" = "N") {
    const reqInfos = (result.infos as any[]).filter(item => item.workNo).map(item => ({ ...item, forceCompleteYn: forceYn }));
    if (reqInfos.length == 0) {
      kutil.alert("작업이 없습니다.");
      return;
    }

    for (const info of reqInfos) {
      if (!info.jobQty || Number(info.jobQty) < 1) {
        kutil.alert("작업수량을 입력해 주세요.");
        return;
      }
    }

    if (forceYn == "N" && await kutil.confirm("작업완료하시겠습니까?") == false) {
      return false;
    }

    let isErpFail = false;
    const validationMsg = [
      ...validationMsg4completeWork,
      { id: "forceCompleteYn", type: "InvalidStatus", skipAlert: true, msg: () => {
          isErpFail = true;
          return "";
        }
      }
    ];

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=completeWork", reqInfos, {
        validationMsg: validationMsg
      });
      router.reload();
    } catch(e) {
      if (isErpFail && await kutil.confirm("ERP I/F를 실패하였습니다.\r\n작업완료(강제완료) 하시겠습니까?")) {
        await completeWork("Y");
      }
      console.log(e);
    }
  }

  async function completeWorkForce() {
    if (await kutil.confirm("ERP I/F를 실패하였습니다.\r\n작업완료(강제완료) 하시겠습니까?") == false) {
      return false;
    }
    await completeWork("Y");
  }

  async function reIn() {
    if (!skuInfo.skuNo) {
      kutil.alert("SKU가 없습니다.");
      return;
    }

    const reqInfos = (result.infos as any[]).filter(item => item.workNo)
    if (reqInfos.length > 0) {
      if (await kutil.confirm("완료처리되지 않은 작업이 있습니다.\r\n재입고하시겠습니까?") == false) {
        return false;
      }
    }

    if ((skuInfo.skuType == "WP") || (skuInfo.skuType == "WB" && skuInfo.skuSize == "MM")) {
      openPopupLoadcapaRate();
    } else {
      reInProc()
    }
  }

  async function reInProc() {
    const reqInfos = (result.infos as any[]).filter(item => item.workNo)
    if (reqInfos.length == 0) {
      if (await kutil.confirm("재입고하시겠습니까?") == false) {
        return false;
      }
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=reIn", {workStationId: searchParams.workStationId}, {
        validationMsg: validationMsg4reIn
      });
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function planIn() {
    if (!skuInfo.skuNo) {
      kutil.alert("SKU가 없습니다.");
      return;
    }

    openPopupInboundInfo();
  }

  async function planInProc(targetInfos: any[]) {
    try {
      const editMode = targetInfos[0].workReqType == "ERP" ? "orderWorkErp" : "orderWorkEtc";
      targetInfos[0].skuNo = skuInfo.skuNo;
      targetInfos[0].workStationId = searchParams.workStationId;
      targetInfos[0].choiceSkuType = "S";
      targetInfos[0].choiceSkuCallQty = "1";
      targetInfos[0].choiceSkuStockQty = "1";
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=" + editMode, targetInfos[0], {
        validationMsg: validationMsg4orderWork
      });
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function holdSku() {
    if (!skuInfo.skuNo) {
      kutil.alert("SKU가 없습니다.");
      return;
    }

    if (await kutil.confirm("보류하시겠습니까?") == false) {
      return false;
    }

    try {
      skuInfo.workStationId = searchParams.workStationId;
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=holdSku", skuInfo, {
        validationMsg: validationMsg4holdSku
      });
      await kutil.alert("보류하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function clearSkuHold() {
    openPopupSkuRelease();
  }

  async function inSku() {
    openPopupSkuAdd();
  }

  async function updateStockQty() {
    const reqInfos = (result.infos as any[]).filter(item => !item.workNo);
    if (reqInfos.length == 0) {
      kutil.alert("수정할 재고가 없습니다.");
      return;
    }

    if (await kutil.confirm("재고수정하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=updateStockQty", reqInfos, {
        validationMsg: validationMsg4updateStockQty
      });
      await kutil.alert("재고수정하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function cancelWork(info: any) {
    if (await kutil.confirm("작업취소하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=cancelWork", info, {
      });
      await kutil.alert("작업취소하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function orderWork(info: any) {
    const stockInfo = (resultRef.current.infos as any[]).find(item =>
      !item.workNo &&
      nz(item.mngType) === nz(info.mngType) &&
      nz(item.wh)      === nz(info.wh) &&
      nz(item.pn)      === nz(info.pn) &&
      nz(item.trNo)    === nz(info.trNo) &&
      nz(item.sn)      === nz(info.sn)
    );
    if (!stockInfo) {
      kutil.alert("재고 정보를 찾을 수 없습니다.");
      return;
    }

    if (info.workReqType === "ERP") {
      const wo = (info.wo ?? "").trim();
      const toSubinv = (info.toSubinv ?? "").trim();
      const isCWo = wo.toUpperCase().startsWith("C");
      // To Subinv가 없고, C로 시작하는 W/O(예외)도 아니면 작업지시 차단
      if (!toSubinv && !isCWo) {
        kutil.alert("To Subinv 정보가 없습니다.");
        return;
      }
    }

    if (Number(info.remainQty) > Number(stockInfo.stockQty)) {
      kutil.alert("재고수량을 초과하였습니다.");
      return;
    }

    try {
      const editMode = info.workReqType == "ERP" ? "orderWorkErp" : "orderWorkEtc";
      info.skuNo = skuInfoRef.current.skuNo;
      info.workStationId = searchParamsRef.current.workStationId;
      info.choiceSkuType = "S";
      info.choiceSkuCallQty = "1";
      info.choiceSkuStockQty = "1";
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=" + editMode, info, {
        validationMsg: validationMsg4orderWork
      });
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function updateSkuNo(info: any) {
    if (await kutil.confirm("SKU를 변경하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=updateSkuNo", info, {
      });
      await kutil.alert("변경하였습니다.");
      updateSearchParams({ workStationId: info.workStationId });
    } catch(e) {
      console.log(e);
    }
  }

  async function updateTrSn(info: any) {
    openPopupTrSnChange(info);
  }

  async function printTrtag(info: any) {
    await printTrtagRef.current?.print([
      "/api/inoutbound/inout_work.download?mode=downloadTrFile&workReqErpNo=" + encodeURIComponent(info.workReqErpNo),
    ]);
  }
  
  async function printLabel(info: any) {
    await printLabelRef.current?.print([info]);
  }
  
  async function printTicket(info: any) {
    await printTicketRef.current?.print({
      infos: [info],
      ticketType: "1",
    });
  }

  async function openPopupInboundInfo() {
    setState(prev => ({ ...prev, isShowInboundInfo: true }));
  }

  async function closePopupInboundInfo(data: any) {
    setState(prev => ({ ...prev, isShowInboundInfo: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupSkuRelease() {
    setState(prev => ({ ...prev, isShowSkuRelease: true }));
  }

  async function closePopupSkuRelease(data: any) {
    setState(prev => ({ ...prev, isShowSkuRelease: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupSkuAdd() {
    setState(prev => ({ ...prev, isShowSkuAdd: true }));
  }

  async function closePopupSkuAdd(data: any) {
    setState(prev => ({ ...prev, isShowSkuAdd: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupSkuCell() {
    setState(prev => ({ ...prev, isShowSkuCell: true }));
  }

  async function closePopupSkuCell(data: any) {
    setState(prev => ({ ...prev, isShowSkuCell: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupLoadcapaRate() {
    setState(prev => ({ ...prev, isShowLoadcapaRate: true }));
  }

  async function closePopupLoadcapaRate(data: any) {
    setState(prev => ({ ...prev, isShowLoadcapaRate: false }));

    if (data?.isSuccess) {
      reInProc();
    }
  }

  async function openPopupTrSnChange(info: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: true, trSnInfo: info}));
  }

  async function closePopupTrSnChange(data: any) {
    setState(prev => ({ ...prev, isShowTrSnChange: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[])?.some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          updateSearchParams({ workStationId: value });
          return;
        }
        default:
          return;
      }
    }

    if (code == "WORK_COMPLETE") {
      completeWork();
    } else if (code == "RE_INBOUND") {
      reIn();
    } else {
      const data = await kutil.sendGet("/api/inoutbound/workstation_manage.data?mode=searchWorkReq", {trackingId: code}, {});
      if (data.infos.length === 0) {
        kutil.alert("Tracking ID에 해당하는 입고예정정보가 없습니다.");
      } else if (data.infos.length === 1) {
        planInProc(data.infos);
      }
    }
  }

  async function onKeyboardShortcut(shortcut: string) {
    if (shortcut == "F1") {
      doSearch(null, null);
    } else if (shortcut == "F2") {
      completeWork();
    } else if (shortcut == "F3") {
      reIn();
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>작업대</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Tooltip title="단축키 [F1]">
              <Button variant="outlined" onClick={doSearch}>조회</Button>
            </Tooltip>
            { auth.write &&
            <Button variant="outlined" onClick={inSku}>SKU전입고</Button>
            }
            { auth.write &&
            <div className="btn_badges">
              {Number(state.skuHoldQty) > 0 && <span>{state.skuHoldQty}</span>}
              <Button variant="outlined" onClick={clearSkuHold}>SKU보류해제</Button>
            </div>
            }
            { auth.write &&
            <Button variant="outlined" onClick={holdSku}>SKU보류</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={planIn}>입고예정</Button>
            }
            { auth.write &&
            <Tooltip title="단축키 [F3]">
              <Button variant="outlined" onClick={reIn}>재입고</Button>
            </Tooltip>
            }
            { auth.write &&
            <Tooltip title="단축키 [F2]">
              <Button variant="contained" onClick={() => completeWork()}>작업완료</Button>
            </Tooltip>
            }
            { auth.write &&
            <Button variant="contained" onClick={completeWorkForce}>작업완료(강제완료)</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* SKU정보 */}
        <div className="sku-info-area">
          <div>
            <p>작업구분</p>
            <div>
              { commonCode.getCodeValue("workType", skuInfo.workType) }
            </div>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>작업대</p>
            <div>
              { commonCode.getCodeValue("workStationId", searchParams.workStationId) }
            </div>
          </div>
          <div className="divider ver"></div>
          <div className="sku_info">
            <div>
              <p>SKU No</p>
              <Input
                inputProps={{ readOnly: true }}
                value={skuInfo.skuNoEx}
              />
            </div>
            <div>
              <p>Track No</p>
              <Input
                inputProps={{ readOnly: true }}
                value={skuInfo.workNoMfc}
              />
            </div>
            <div>
              <p>작업자</p>
              <Input
                inputProps={{ readOnly: true }}
                value={skuInfo.workRequesterName}
              />
            </div>
            <div>
              <p>SKU Count</p>
              <Input
                inputProps={{ readOnly: true }}
                value={[skuInfo.workReqSortOrder, skuInfo.workReqTotalQty].filter(Boolean).join("/")}
              />
            </div>
            <div>
              <p> SKU Type</p>
              <Input
                inputProps={{ readOnly: true }}
                value={commonCode.getCodeValue("skuType", skuInfo.skuType)}
              />
            </div>
            <div>
              <p>SKU Size</p>
              <Input
                inputProps={{ readOnly: true }}
                value={commonCode.getCodeValue("skuSize", skuInfo.skuSize)}
              />
            </div>
            <div>
              <p>SKU Cell</p>
              <div className={state.isHoverBtn ? "input-btn on" : "input-btn"}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={valueFormatter.number(skuInfo.skuCellQty ?? "")}
                />
                {skuInfo.skuNo != "" && auth.func1 &&
                <button
                  onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn: true }))}
                  onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn: false }))}
                  onClick={openPopupSkuCell}
                >
                  <Tooltip title="수정">
                    <img src={modifyIcon} alt="수정" />
                  </Tooltip>
                </button>
                }
              </div>
            </div>
          </div>
          <span>
            <em>
              <Barcode value={state.barcodeType} format="CODE128" width={1} height={28} displayValue={false} background="#f7f7f7" margin={0} />
              <div>
                <i></i>
                <FormControl>
                  <RadioGroup
                    row
                    value={state.barcodeType}
                    onChange={(e) => {setState(prev => ({ ...prev, barcodeType: e.target.value }))}}
                  >
                    <FormControlLabel value="WORK_COMPLETE" control={<Radio size="small" />} label="작업완료" />
                    <FormControlLabel value="RE_INBOUND" control={<Radio size="small" />} label="재입고" />
                  </RadioGroup>
                </FormControl>
              </div>
            </em>
          </span>
        </div>
        {/*// SKU정보 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          { auth.func2 &&
          <Button variant="additional" size="small" onClick={updateStockQty}>재고수정</Button>
          }
          <div className="workstation">
            <p>작업대</p>
            <FormControl size="small">
              <Select
                value={searchParams.workStationId}
                onChange={(e) => {doSearch(e, {workStationId: e.target.value});}}
                displayEmpty
              >
              {(commonCode.workStationId as any[])?.map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* SKU 재고정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={defColDef}
              initialState={gridState}
              getRowStyle={gridOptions.getRowStyle}
              stopEditingWhenCellsLoseFocus={gridOptions.stopEditingWhenCellsLoseFocus}
              showInfo={false}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// SKU 재고정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>출고 예정 정보</p>
        </div>
        {/* 출고 예정 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo4out}
              rowData={result.outInfos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState4out}
              showInfo={false}
              onStateUpdated={gridCallbacks4out.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 출고 예정 정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>작업 목록</p>
        </div>
        {/* 작업 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo4equip}
              rowData={result.equipInfos}
              defaultColDef={defColDef4equip}
              initialState={gridState4equip}
              showInfo={false}
              onStateUpdated={gridCallbacks4equip.onStateUpdated}
              onRowClicked={gridCallbacks4equip.onRowClicked}
            />
          </div>
        </AgGridProvider>
        {state.isShowInboundInfo && <InboundInfoPopup isShow={state.isShowInboundInfo} onClose={closePopupInboundInfo} id={props.id} data={{info: skuInfo, workStationId: searchParams.workStationId}} />}
        {state.isShowSkuRelease && <SkuReleasePopup isShow={state.isShowSkuRelease} onClose={closePopupSkuRelease} id={props.id}data={{skuNo: skuInfo.skuNo, workStationId: searchParams.workStationId}} />}
        {state.isShowSkuAdd && <SkuAddPopup isShow={state.isShowSkuAdd} onClose={closePopupSkuAdd} id={props.id} data={{workStationInfo: workStationInfo}} />}
        {state.isShowSkuCell && <SkuCellPopup isShow={state.isShowSkuCell} onClose={closePopupSkuCell} id={props.id} data={{info: skuInfo}} />}
        {state.isShowLoadcapaRate && <LoadcapaRatePopup isShow={state.isShowLoadcapaRate} onClose={closePopupLoadcapaRate} id={props.id} data={{info: skuInfo}} />}
        {state.isShowTrSnChange && <TrSnChangePopup isShow={state.isShowTrSnChange} onClose={closePopupTrSnChange} id={props.id} data={{path: "workstation_manage", info: state.trSnInfo}} />}
        <PrintTrtag ref={printTrtagRef} />
        <PrintLabel ref={printLabelRef} />
        <PrintTicket ref={printTicketRef} />
        {/*// 작업 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(WorkstationManage);
